import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Send, BookOpen, RefreshCw, AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { BookRecommendationCard } from './BookRecommendationCard';
import { BookRecommendationCardSkeleton } from './BookRecommendationCardSkeleton';
import { useRecommendationFeedback } from '@/hooks/useRecommendationFeedback';
import type { BookRecommendation } from '@/hooks/useBookRecommendations';
import { cn } from '@/lib/utils';

const CHAT_STORAGE_KEY = 'smart-book-chat-history';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  recommendations?: BookRecommendation[];
  timestamp: Date;
  isError?: boolean;
}

// Load chat history from localStorage
const loadChatHistory = (): ChatMessage[] => {
  try {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (e) {
    console.warn('Failed to load chat history:', e);
  }
  return [];
};

// Save chat history to localStorage
const saveChatHistory = (messages: ChatMessage[]) => {
  try {
    // Only save last 20 messages to avoid storage bloat
    const toSave = messages.slice(-20);
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('Failed to save chat history:', e);
  }
};

export function SmartBookChat() {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadChatHistory());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fromTBROnly, setFromTBROnly] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { submitFeedback } = useRecommendationFeedback();

  // Persist chat history when messages change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(messages);
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = useCallback(async (messageOverride?: string) => {
    const messageToSend = messageOverride || input.trim();
    if (!messageToSend || loading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageToSend,
      timestamp: new Date(),
    };

    // Only add user message if not a retry
    if (!messageOverride) {
      setMessages(prev => [...prev, userMessage]);
      setInput('');
    }
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to get recommendations');
        setLoading(false);
        return;
      }

      // Build conversation context for the AI (excluding error messages)
      const conversationContext = messages
        .filter(m => !m.isError)
        .map(m => ({
          role: m.role,
          content: m.role === 'user' 
            ? m.content 
            : m.recommendations 
              ? `I recommended: ${m.recommendations.map(r => `"${r.title}" by ${r.author}`).join(', ')}. ${m.content}`
              : m.content
        }));

      const { data, error } = await supabase.functions.invoke('recommend-books-chat', {
        body: { 
          message: messageToSend,
          conversationHistory: conversationContext,
          fromTBROnly,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message || 'Here are some recommendations for you:',
        recommendations: data.recommendations,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('❌ Chat error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isRateLimit = errorMessage.includes('429') || errorMessage.includes('rate limit');
      const isPayment = errorMessage.includes('402') || errorMessage.includes('payment');
      
      let userFriendlyMessage = "Sorry, I couldn't process that request.";
      if (isRateLimit) {
        userFriendlyMessage = "I'm getting too many requests right now. Please wait a moment and try again.";
      } else if (isPayment) {
        userFriendlyMessage = "The AI service is temporarily unavailable. Please try again later.";
      }
      
      // Add error message to chat
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: userFriendlyMessage,
        timestamp: new Date(),
        isError: true,
      }]);
      
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, fromTBROnly]);

  const handleRetry = useCallback(() => {
    // Find the last user message
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    if (lastUserMessage) {
      // Remove the last error message if present
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.isError) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      handleSend(lastUserMessage.content);
    }
  }, [messages, handleSend]);

  const handleDismissRecommendation = async (messageId: string, rec: BookRecommendation) => {
    // Submit feedback
    await submitFeedback({
      bookId: rec.bookId,
      bookTitle: rec.title,
      bookAuthor: rec.author,
      isbn13: rec.isbn13,
      isbn10: rec.isbn10,
      feedbackType: 'dismissed',
      recommendationReason: rec.reason,
      sessionId: rec.sessionId,
    });

    // Remove from message
    setMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.recommendations) {
        return {
          ...msg,
          recommendations: msg.recommendations.filter(r => r.title !== rec.title),
        };
      }
      return msg;
    }));

    // Add a follow-up asking for alternatives
    setInput(`Not "${rec.title}" - can you suggest something different?`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(CHAT_STORAGE_KEY);
    setRetryCount(0);
  };

  const suggestedPrompts = [
    "Something cozy for a rainy day",
    "A gripping thriller I can't put down",
    "Books similar to what I've loved recently",
    "Help me pick from my TBR pile",
  ];

  const lastMessageIsError = messages.length > 0 && messages[messages.length - 1]?.isError;

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Smart Book Chat</CardTitle>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearChat} title="Clear chat history">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear
            </Button>
          )}
        </div>
        <CardDescription>
          Have a conversation to find your next perfect read
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* TBR Toggle */}
        <div className="flex items-center space-x-2 pb-2">
          <Switch
            id="tbr-mode"
            checked={fromTBROnly}
            onCheckedChange={setFromTBROnly}
          />
          <Label htmlFor="tbr-mode" className="text-sm cursor-pointer">
            <BookOpen className="w-4 h-4 inline mr-1" />
            Recommend from my TBR only
          </Label>
        </div>

        {/* Chat Messages */}
        {messages.length > 0 ? (
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex flex-col gap-2",
                  message.role === 'user' ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "rounded-lg px-4 py-2 max-w-[85%]",
                    message.role === 'user'
                      ? "bg-primary text-primary-foreground"
                      : message.isError
                        ? "bg-destructive/10 border border-destructive/20"
                        : "bg-muted"
                  )}
                >
                  {message.isError && (
                    <AlertCircle className="w-4 h-4 text-destructive inline mr-2" />
                  )}
                  <p className="text-sm whitespace-pre-wrap inline">{message.content}</p>
                </div>
                
                {message.recommendations && message.recommendations.length > 0 && (
                  <div className="w-full space-y-2 mt-2">
                    {message.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-2 rounded-lg bg-background/50 hover:bg-background transition-colors">
                        <BookRecommendationCard
                          {...rec}
                          compact
                          onDismiss={() => handleDismissRecommendation(message.id, rec)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading State */}
            {loading && (
              <div className="flex flex-col gap-2 items-start">
                <div className="rounded-lg px-4 py-2 bg-muted animate-pulse">
                  <p className="text-sm text-muted-foreground">Thinking...</p>
                </div>
                <div className="w-full space-y-2 mt-2">
                  {[...Array(3)].map((_, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-background/50">
                      <div className="w-16 aspect-[2/3] bg-muted rounded animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                        <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Retry Button for Errors */}
            {lastMessageIsError && !loading && (
              <div className="flex justify-center">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  className="gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try again
                </Button>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        ) : (
          /* Suggested Prompts */
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => setInput(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="flex gap-2 pt-2 border-t">
          <Textarea
            placeholder={fromTBROnly 
              ? "What should I read from my TBR?" 
              : "What kind of book are you in the mood for?"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[60px] max-h-[120px] resize-none"
            disabled={loading}
          />
          <Button
            onClick={() => handleSend()}
            disabled={loading || !input.trim()}
            size="icon"
            className="h-auto aspect-square"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}